import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, FileBadge, RefreshCw, Paperclip } from "lucide-react";
import { uploadFile, openBlob } from "@/lib/upload";
import { toast } from "sonner";

export const DOC_TYPES = {
  employee: { patente: "Patente", visita_medica: "Visita medica", corso_sicurezza: "Corso sicurezza", altro: "Altro" },
  vehicle: { assicurazione: "Assicurazione", revisione: "Revisione", bollo: "Bollo", altro: "Altro" },
};

function daysLeft(date) {
  const t = new Date(); t.setHours(0, 0, 0, 0);
  return Math.round((new Date(date + "T00:00:00") - t) / 86400000);
}

function StatusBadge({ date }) {
  const d = daysLeft(date);
  const [cls, txt] = d < 0 ? ["bg-rose-100 text-rose-700", "Scaduto"]
    : d <= 30 ? ["bg-amber-100 text-amber-800", d === 0 ? "Scade oggi" : `Tra ${d} gg`]
    : ["bg-emerald-100 text-emerald-700", "Valido"];
  return <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${cls}`}>{txt}</span>;
}

function DocFiles({ d, onChange }) {
  const [pct, setPct] = useState(null);
  const add = async (file) => {
    try {
      setPct(0);
      const upload_id = await uploadFile(file, setPct);
      await api.post(`/documents/${d.id}/files`, { upload_id });
      toast.success("Scansione allegata"); onChange();
    } catch (e) { toast.error(formatApiError(e)); } finally { setPct(null); }
  };
  const del = async (fid) => { await api.delete(`/documents/${d.id}/files/${fid}`); onChange(); };
  return (
    <div className="flex flex-wrap items-center gap-2 mt-2">
      {(d.files || []).map((f) => (
        <span key={f.id} className="inline-flex items-center gap-1 text-xs bg-slate-100 rounded-full px-3 py-1">
          <button onClick={() => openBlob(`/documents/${d.id}/files/${f.id}`).catch((e) => toast.error(formatApiError(e)))} className="hover:text-sky-700" data-testid={`document-file-${f.id}`}>{f.filename}</button>
          <button onClick={() => del(f.id)} className="text-rose-500 ml-1" data-testid={`document-file-delete-${f.id}`}>×</button>
        </span>
      ))}
      <label className="text-xs font-semibold text-sky-700 cursor-pointer inline-flex items-center gap-1">
        <Paperclip className="w-3.5 h-3.5" /> {pct !== null ? `Caricamento ${pct}%` : "Allega scansione"}
        <input type="file" accept="application/pdf,image/*" capture="environment" className="hidden" disabled={pct !== null}
          onChange={(e) => { const f = e.target.files[0]; e.target.value = ""; if (f) { add(f); } }} data-testid={`document-attach-${d.id}`} />
      </label>
    </div>
  );
}

const emptyForm = (ownerType) => ({ owner_type: ownerType, owner_id: "", doc_type: Object.keys(DOC_TYPES[ownerType])[0], label: "", number: "", expiry_date: "" });

export default function DocumentsPanel({ admin = false, owners = null }) {
  const [list, setList] = useState([]);
  const [form, setForm] = useState(emptyForm("employee"));
  const load = () => api.get("/documents").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try { await api.post("/documents", form); toast.success("Documento salvato"); setForm(emptyForm(form.owner_type)); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Eliminare il documento?")) return;
    try { await api.delete(`/documents/${id}`); load(); } catch (err) { toast.error(formatApiError(err)); }
  };
  const renew = async (d) => {
    const v = window.prompt("Nuova data di scadenza (GG/MM/AAAA)", d.expiry_date.split("-").reverse().join("/"));
    const m = v && v.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (!v) return;
    if (!m) return toast.error("Formato data non valido");
    const iso = `${m[3]}-${m[2].padStart(2, "0")}-${m[1].padStart(2, "0")}`;
    try { await api.put(`/documents/${d.id}`, { expiry_date: iso }); toast.success("Scadenza aggiornata"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const ownerOptions = admin && owners ? owners[form.owner_type] : [];

  return (
    <div className="space-y-5" data-testid="documents-panel">
      <form onSubmit={save} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 border border-slate-200 rounded-xl p-4 bg-white" data-testid="document-form">
        {admin && (
          <>
            <div><Label>Di chi è</Label>
              <select className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white" value={form.owner_type}
                onChange={(e) => setForm(emptyForm(e.target.value))} data-testid="document-owner-type">
                <option value="employee">Dipendente</option><option value="vehicle">Mezzo</option>
              </select></div>
            <div><Label>{form.owner_type === "employee" ? "Dipendente" : "Mezzo"}</Label>
              <select required className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white" value={form.owner_id}
                onChange={(e) => setForm({ ...form, owner_id: e.target.value })} data-testid="document-owner-select">
                <option value="">Scegli…</option>
                {ownerOptions.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
              </select></div>
          </>
        )}
        <div><Label>Documento</Label>
          <select className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white" value={form.doc_type}
            onChange={(e) => setForm({ ...form, doc_type: e.target.value })} data-testid="document-type-select">
            {Object.entries(DOC_TYPES[form.owner_type]).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select></div>
        <div><Label>Nome (opz.)</Label><Input value={form.label} placeholder="es. Patente C" onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="document-label-input" /></div>
        <div><Label>Numero (opz.)</Label><Input value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })} data-testid="document-number-input" /></div>
        <div><Label>Scadenza</Label><Input type="date" required value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} data-testid="document-expiry-input" /></div>
        <div className="flex items-end"><Button type="submit" className="bg-sky-600 hover:bg-sky-700 text-white w-full" data-testid="document-save-button"><Plus className="w-4 h-4 mr-2" /> Aggiungi</Button></div>
      </form>

      {list.length === 0 ? <p className="text-sm text-slate-400">Nessun documento inserito.</p> : (
        <ul className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100" data-testid="documents-list">
          {list.map((d) => (
            <li key={d.id} className="p-4 flex items-center gap-3" data-testid={`document-${d.id}`}>
              <FileBadge className="w-5 h-5 text-sky-600 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm text-slate-900 flex flex-wrap items-center gap-2">
                  {d.label || DOC_TYPES[d.owner_type][d.doc_type]} <StatusBadge date={d.expiry_date} />
                </div>
                <div className="text-xs text-slate-500">{admin && `${d.owner_name} · `}Scade il {d.expiry_date.split("-").reverse().join("/")}{d.number && ` · N. ${d.number}`}</div>
                <DocFiles d={d} onChange={load} />
              </div>
              <button onClick={() => renew(d)} title="Rinnova" className="p-2 text-slate-400 hover:text-sky-600" data-testid={`renew-document-${d.id}`}><RefreshCw className="w-4 h-4" /></button>
              <button onClick={() => remove(d.id)} className="p-2 text-slate-400 hover:text-rose-600" data-testid={`delete-document-${d.id}`}><Trash2 className="w-4 h-4" /></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
